import './NoteModal.scss';

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import { Map } from 'immutable';
import humps from 'humps';
import { useFormik } from 'formik';
import yup from 'src/utils/yup';
import NotesIcon from '@mui/icons-material/BorderColorSharp';
import Button from 'src/component/UI/Button';
import DeviceSelectorField from 'src/component/UI/DeviceSelectorField';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { getSingularWorkTicketLabel } from 'src/module/authentication/selector';
import { useTranslation } from 'src/i18n';

type Note = {
  deviceId: string,
  subject: string,
  note: string,
  workTicket?: string,
  alarm?: string,
  expiredAt: Date,
  noteCreatedAt: Date,
};

export default function NoteModal (props) {
  const { t } = useTranslation();
  const noteTypeRef = React.useRef(props.noteType); // persistent note type
  const initialNoteTypeRef = React.useRef(props.noteType); // initial note type set by parent
  const [noteType, setNoteType] =  useState(props.noteType); // note type selected by user
  const singularWorkTicketLabel = useSelector(getSingularWorkTicketLabel);

  const modalTitlesByNoteType = {
    note: t('notes.addNote'),
    loto: t('notes.addLoto'),
    nuisance: t('notes.addNuisance'),
    facility: t('notes.addNote'),
    any: t('notes.addNote')
  };

  const formik = useFormik({
    initialValues: {
      deviceId: props.deviceId,
      subject: '',
      note: initialNoteTypeRef.current === 'any' ? 'note' : initialNoteTypeRef.current,
      workTicket: '',
      alarm: '',
      showAlarm: noteTypeRef.current === 'nuisance',
      expiredAt: moment().format('YYYY-MM-DD'),
      createdAt: moment().format('YYYY-MM-DD'),
    },
    onSubmit: values => {
      const note: Note = {
        subject: values.subject,
        note: values.note,
        expiredAt: moment(values.expiredAt, 'YYYY-MM-DD').toDate(),
        noteCreatedAt: moment(values.createdAt, 'YYYY-MM-DD').toDate(),
      };
      if (values.deviceId?.trim()) {
        note.deviceId = values.deviceId;
      }
      if (values.workTicket.trim() !== '') {
        note.workTicket = values.workTicket;
      }
      if (values.alarm.trim() !== '') {
        note.alarm = values.alarm;
      }
      props.handleSubmit(Map(humps.decamelizeKeys(note)));
      formik.resetForm();
    },
    validationSchema: () => {
      return yup.object().shape({
        deviceId: noteTypeRef.current !== 'facility'
          ? yup.string('Device').required(t('notes.deviceRequired'))
          : yup.string('Device'),
        note: yup
          .string(t('common.description'))
          .required(),
        workTicket: yup
          .string(singularWorkTicketLabel || t('workTickets.singular')),
        showAlarm: yup
          .bool(),
        alarm: yup
          .string(t('notes.alarm'))
          .when("showAlarm", {
            is: true,
            then: yup.string().required(t('notes.alarmRequired'))
          }),
        expiredAt: yup
          .string(t('notes.expiresOn')),
        createdAt: yup
          .string(t('notes.createdOn')),
      });
    },
  });

  const handleChangeDeviceId = (value) => {
    if ( value?.id && value?.id.trim() !== '' ) {
      formik.setFieldValue('deviceId', value.id, false);
    }
  };

  const handleChangeNote = (event) => {
    const val = event.target.value as string;
    formik.setFieldValue('note', val, false);
    noteTypeRef.current = val;
    setNoteType(val);
  };

  const clearValues = () => {
    formik.resetForm();
  };

  useEffect(() => {
    noteTypeRef.current = props.noteType ? props.noteType : 'note';
    formik.setFieldValue('showAlarm', noteTypeRef.current === 'nuisance' || noteType === 'nuisance', false);
  }, [props.noteType]);

  useEffect(() => {
    handleChangeDeviceId(props.deviceId);
  }, [props.deviceId]);

  useEffect(() => {
    if (noteType === 'nuisance') {
      formik.setFieldValue('showAlarm', true, false);
    }
  }, [noteType]);

  // this will clear the formik values when the component is unmounted
  // to make sure we're not persisting the state of the last note
  useEffect(() => {
    return clearValues;
  }, []);


  return (
    <Modal
      data-testid='note-modal'
      open={props.open}
      onClose={props.handleClose}
      aria-labelledby='note-modal-title'
    >
      <div id='notes-model' className='modal note-modal'>
        <h2 id='note-modal-title'>{modalTitlesByNoteType[props.noteType]}</h2>
        {(noteType === 'loto' || noteType === 'nuisance') && <p className='subtitle'>{t('notesList.createLotoNuisance')}</p>}
        <form noValidate autoComplete='off' onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item container direction="column" spacing={3} md={6}>
              {props.hideDeviceSelector ? null : <DeviceSelectorField deviceId={props.deviceId} readonly={false}
                onChange={handleChangeDeviceId} error={Boolean(formik.errors.deviceId)} helperText={formik.errors.deviceId} />}
              <FormControl className='alarm-select-field' variant='outlined'>
                <InputLabel id={`note-input-label`} error={Boolean(formik.errors.note)}>
                  {t('common.type')}
                </InputLabel>
                <Select
                  id={'note'}
                  labelId={`note`}
                  name={'note'}
                  label={t('common.type')}
                  value={noteTypeRef.current == 'any' ? 'note' : noteTypeRef.current}
                  error={Boolean(formik.errors.note)}
                  onChange={handleChangeNote}
                  disabled={initialNoteTypeRef.current !== 'any'}
                >
                  <MenuItem value={'note'}>{t('notes.note')}</MenuItem>
                  <MenuItem value={'loto'}>{t('notes.loto')}</MenuItem>
                  <MenuItem value={'nuisance'}>{t('notes.nuisance')}</MenuItem>
                  <MenuItem value={'facility'}>{t('notes.facility')}</MenuItem>
                </Select>
                <FormHelperText error={Boolean(formik.errors.note)} >
                  {(formik.errors.note ?
                    <>
                      {formik.errors.note}<br />
                    </> : null)}
                </FormHelperText>
              </FormControl>
              {
                noteTypeRef.current === 'loto' || noteType === 'loto' ?
                  (<TextField id='workTicket' name='workTicket' value={formik.values.workTicket} label={singularWorkTicketLabel || t('workTickets.singular')}
                    onChange={formik.handleChange} error={Boolean(formik.errors.workTicket)} helperText={formik.errors.workTicket} inputProps={{ maxLength: 255 }} InputLabelProps={{ shrink: false }} />)
                  : noteTypeRef.current === 'nuisance' || noteType === 'nuisance' ?
                    (<FormControl className='alarm-select-field' variant='outlined'>
                      <InputLabel id={`alarm-input-label`} error={Boolean(formik.errors.alarm)}>
                        {t('notes.alarm')}
                      </InputLabel>
                      <Select
                        id={'alarm-select'}
                        labelId={`alarm-select-label`}
                        name={'alarm'}
                        label={t('notes.alarm')}
                        value={formik.values.alarm}
                        error={Boolean(formik.errors.alarm)}
                        onChange={formik.handleChange}
                      >
                        <MenuItem value={'low temp'}>{t('notes.lowTemp')}</MenuItem>)
                        <MenuItem value={'low load'}>{t('notes.lowLoad')}</MenuItem>)
                      </Select>
                      <FormHelperText error={Boolean(formik.errors.alarm)} >
                        {(formik.errors.alarm ?
                          <>
                            {formik.errors.alarm}<br />
                          </> : null)}
                      </FormHelperText>
                    </FormControl>) : null
              }
              {noteTypeRef.current !== 'facility' && <TextField id='expiredAt' name='expiredAt' value={formik.values.expiredAt} label={t('notes.expiresOn')} type={'date'}
                onChange={formik.handleChange} error={Boolean(formik.errors.expiredAt)} helperText={formik.errors.expiredAt} inputProps={{ maxLength: 64 }} InputLabelProps={{ shrink: false }} />}
              <TextField id='createdAt' name='createdAt' value={formik.values.createdAt} label={t('notes.createdOn')} InputLabelProps={{ shrink: false }}  disabled={true} />
            </Grid>
            <Grid item container direction="column" spacing={3} md={6}>
              <TextField id='subject' name='subject' value={formik.values.subject} label={t('notes.note')}
                onChange={formik.handleChange} error={Boolean(formik.errors.subject)}
                helperText={formik.errors.subject} inputProps={{ maxLength: 255 }} InputLabelProps={{ shrink: false }}
                multiline minRows={9} />
            </Grid>
          </Grid>
          <div className='button-bar'>
            <Button onClick={props.handleClose}>{t('common.cancel')}</Button>
            <Button type='submit' icon={<NotesIcon style={{ fontSize: 14 }} />} cta>{t('common.submit')}</Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

NoteModal.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  noteType: PropTypes.oneOf(['note', 'loto', 'nuisance', 'facility', 'any', 'all']),
  readOnlyCircuit: PropTypes.bool,
  hideDeviceSelector: PropTypes.bool
};

NoteModal.defaultProps = {
  deviceId: '',
  noteType: 'note',
  readOnlyCircuit: false,
  hideDeviceSelector: false
};
